import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Droplet, Eye, TrendingUp } from 'lucide-react';

const HairAnalysisReport = ({ analysis, loading }) => {
  if (loading) {
    return (
      <div className="w-full py-12 flex flex-col items-center justify-center space-y-4" data-testid="analysis-loading">
        <div className="w-16 h-16 border-4 border-accent-primary border-t-transparent rounded-full animate-spin" />
        <p className="font-sans text-text-secondary">Analyzing your hair...</p>
      </div>
    );
  }

  if (!analysis) {
    return null;
  }

  const stats = [
    {
      icon: <Sparkles className="h-6 w-6" />,
      label: 'Current Level',
      value: analysis.current_level,
      color: 'bg-accent-primary'
    },
    {
      icon: <Droplet className="h-6 w-6" />,
      label: 'Current Tone',
      value: analysis.current_tone,
      color: 'bg-accent-secondary'
    },
    {
      icon: <Eye className="h-6 w-6" />,
      label: 'Texture',
      value: analysis.texture,
      color: 'bg-text-secondary'
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      label: 'Thickness',
      value: analysis.thickness,
      color: 'bg-text-primary'
    },
  ];

  return (
    <div className="w-full space-y-6" data-testid="hair-analysis-report">
      <div className="text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-text-primary font-light mb-2">
          Your Hair Analysis
        </h2>
        <p className="font-sans text-text-secondary">AI-powered professional assessment</p>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-bg-primary border border-border-color p-6 rounded-sm hover:shadow-md transition-shadow"
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className={`w-12 h-12 ${stat.color} text-white rounded-sm flex items-center justify-center mb-4`}>
              {stat.icon}
            </div>
            <p className="font-sans text-xs text-text-muted uppercase tracking-wider mb-2">
              {stat.label}
            </p>
            <p className="font-serif text-lg text-text-primary font-medium">
              {stat.value}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Undertones */}
      <div className="bg-bg-secondary border border-border-color p-6 rounded-sm">
        <h3 className="font-sans text-sm text-text-muted uppercase tracking-wider mb-2">
          Undertones
        </h3>
        <p className="font-serif text-xl text-text-primary" data-testid="undertones-value">
          {analysis.undertones}
        </p>
      </div>

      {/* Recommendations */}
      {analysis.recommendations && analysis.recommendations.length > 0 && (
        <div className="bg-accent-primary/5 border border-accent-primary/20 p-6 rounded-sm">
          <h3 className="font-sans text-sm text-accent-primary uppercase tracking-wider mb-4">
            Recommended Shades
          </h3>
          <div className="space-y-2" data-testid="recommendations-list">
            {analysis.recommendations.map((rec, index) => (
              <div key={index} className="font-sans text-text-primary flex items-center space-x-2">
                <span className="text-accent-primary">•</span>
                <span>{rec}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HairAnalysisReport;